import { useMemo } from 'react'
import { gemVar, ThemedColors } from '@opallabs/gem'
import { Button, ButtonSize } from '../../Button'
import { Icon } from '../../Icon'
import { useCascadingMenu } from '../Root'
import type { ItemProps } from '../types'
import { useHandleItemKeyPress } from '../useHandleItemKeypress'
import * as styles from './styles'

export const Item: React.FC<ItemProps> = ({
  childOptions,
  disableChildOptions,
  disableSelection,
  disabled,
  index,
  label,
  leadingNode,
  onDescendMenu,
  onSelect,
  selected,
  trailingNode,
  value,
}) => {
  const { onActiveDescendentChange, setFocusedItemIndex, stackLevel } =
    useCascadingMenu()

  const hasChildren = useMemo(
    () => Boolean(childOptions?.length),
    [childOptions]
  )

  const isDisabled = useMemo(() => {
    if (disabled) return disabled

    return hasChildren && disableSelection && disableChildOptions
  }, [disabled, disableChildOptions, disableSelection, hasChildren])

  const handleDescendMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    onDescendMenu()
  }

  const handleClick = (e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation()
    if (isDisabled) return

    setFocusedItemIndex(index)

    if (hasChildren && disableSelection) {
      return onDescendMenu?.()
    }

    onSelect()
  }

  const { handleKeyPress, itemRef } = useHandleItemKeyPress({
    disabledKeys: !hasChildren ? ['ArrowRight'] : undefined,
    disableSelection,
    hasChildren,
    index,
    itemDisabled: isDisabled,
    onDescendMenu,
    onSelect,
  })

  return (
    <li
      ref={itemRef}
      aria-selected={disableSelection && hasChildren ? undefined : selected}
      aria-disabled={isDisabled}
      aria-expanded={childOptions?.length ? false : undefined}
      aria-labelledby={`${value}-name`}
      aria-level={stackLevel}
      id={styles.composeItemId(value)}
      role="treeitem"
      tabIndex={-1}
      css={[styles.item, styles.itemHoverStyles]}
      onFocus={() => onActiveDescendentChange?.(styles.composeItemId(value))}
      onClick={handleClick}
      onKeyDown={handleKeyPress}
    >
      {leadingNode}
      <span css={styles.itemName}>
        <span id={`${value}-name`} css={styles.itemNameText}>
          {label}
        </span>
        {selected && (
          <Icon
            glyph="gem-check-circle"
            color={gemVar(ThemedColors.Primary6)}
          />
        )}
      </span>
      {trailingNode}
      {hasChildren && (
        <Button
          css={styles.itemButton}
          tabIndex={-1}
          disabled={isDisabled || disableChildOptions}
          name="open"
          variant="ghost"
          size={ButtonSize.Small}
          icon={<Icon glyph="gem-chevron-e" />}
          onClick={handleDescendMenu}
        />
      )}
    </li>
  )
}
Item.displayName = 'Lapidary: CascadingMenu.Item'
